import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Rss, ExternalLink, Calendar, ArrowLeft } from 'lucide-react';
import { getNewsArticle } from '../utils/api';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const NewsArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const data = await getNewsArticle(slug);
        setArticle(data);
      } catch (err) {
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Article Not Found</h1>
        <Link to="/news" className="text-primary hover:underline">
          ← Back to News
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back Link */}
      <Link 
        to="/news" 
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to News
      </Link>

      {/* Article Header */}
      <article className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {article.featured_image && (
          <img 
            src={article.featured_image} 
            alt={article.title}
            className="w-full h-64 sm:h-80 object-cover"
          />
        )}
        
        <div className="p-6 sm:p-8">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-4">
            <span className="flex items-center gap-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full">
              <Rss className="h-3 w-3" />
              {article.source_name}
            </span>
            {article.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(article.published_at)}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
            {article.title}
          </h1>

          {/* Content */}
          <div 
            className="prose prose-slate max-w-none mb-8"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* CTA */}
          <div className="border-t border-slate-200 pt-6">
            <p className="text-slate-600 mb-4">
              This article was curated from {article.source_name}. Read the full original article for complete details.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={article.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors"
              >
                <ExternalLink className="h-5 w-5" />
                Read Full Article on {article.source_name}
              </a>
              <SaveToIdeasButton
                title={article.title}
                category="CLL News"
                sourcePage="/news"
                sourceUrl={`/news/${slug}`}
              />
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default NewsArticlePage;
